'use client'
import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import LanguageSwitcher from '../language-switcher/LanguageSwitcher'

import { PrismicLink } from '@prismicio/react'
import { PrismicNextImage } from '@prismicio/next'

import { useLocale } from '@/context/LocaleContext'
import { getLangPath } from '@/utils/getLangFormat'

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

/** Static marketplace deep links (managed in-repo, not Prismic). The main Marketplace entry is a Prismic Products row. */
const MARKETPLACE_RESOURCES_LINKS = [
  { title: 'GCC High Licensing', href: '/marketplace/microsoft/category/microsoft-365-suites/gcc-high' },
  { title: 'Microsoft License Quote', href: '/marketplace/quote' },
]

const localizedHref = (locale: string, href: string) => {
  const langPath = getLangPath(locale)
  return langPath ? `/${langPath}${href}` : href
}

const FOOTER_LINK_LIST =
  'mt-4 space-y-2 [&>li]:text-base [&>li]:font-normal [&>li]:capitalize [&>li]:text-white [&>li>a]:block [&>li>a]:transition-colors [&>li>a]:duration-200 [&>li>a:hover]:text-primary-green-85'

const FOOTER_LINK_LIST_FRAMEWORKS = `${FOOTER_LINK_LIST} [&>li:last-child]:opacity-50 [&>li:last-child>a:hover]:text-primary-ink-6`

const FOOTER_LINK_LIST_SUPPORT = `${FOOTER_LINK_LIST} [&>li]:flex [&>li]:items-center [&>li]:gap-2 [&>li]:xl:whitespace-nowrap [&>li>a]:inline-block [&>li>a]:w-auto`

const FooterLinks = React.memo(
  ({
    data,
    locales,
    showListLocalesValue,
    onSetShowListLocalesValue,
  }: {
    data: DataObject
    locales?: DataObject
    showListLocalesValue: boolean
    onSetShowListLocalesValue: (value: boolean) => void
  }) => {
    const locale = useLocale()
    return (
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <div className="flex flex-col gap-6 self-start">
          <Image src="/images/sf-logo-2.svg" width={54} height={56} alt="sf-logo-2" loading="lazy" />

          <div className="mt-6">
            <div className="flex items-center gap-2.5 pb-8">
              {data.body.map((slice: any) => {
                if (slice.slice_type === 'social_logos') {
                  return slice.items.map((item: ItemObject, index: number) => {
                    const key = `i-${index}`
                    return (
                      <div key={key} className="flex items-center">
                        <PrismicLink
                          field={item.link}
                          aria-label={item.logo?.alt || 'Social media'}
                          className="group inline-flex h-10 w-10 items-center justify-center rounded border border-white/20 transition-colors duration-200 hover:border-white/40"
                        >
                          <PrismicNextImage
                            field={item.logo}
                            width={18}
                            height={18}
                            className="h-[18px] w-[18px] object-contain opacity-60 transition-opacity duration-200 group-hover:opacity-100"
                            loading="lazy"
                            fallbackAlt=""
                          />
                        </PrismicLink>
                      </div>
                    )
                  })
                }
              })}
            </div>

            <LanguageSwitcher
              currentLang={locale}
              locales={locales}
              showListLocalesValue={showListLocalesValue}
              onSetShowListLocalesValue={onSetShowListLocalesValue}
            />
          </div>
        </div>

        <div>
          <span className="block text-sm text-white opacity-50">Products</span>
          <ul className={FOOTER_LINK_LIST}>
            {data.products.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <li key={key}>
                  <PrismicLink field={item.link_url}>{item.link_title}</PrismicLink>
                </li>
              )
            })}
          </ul>
        </div>

        <div>
          <span className="block text-sm text-white opacity-50">Solutions</span>
          <ul className={FOOTER_LINK_LIST}>
            {data.solutions.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <li key={key}>
                  <PrismicLink field={item.link_url}>{item.link_title}</PrismicLink>
                </li>
              )
            })}
          </ul>

          {/* For Desktop Version */}
          <div className="hidden lg:mt-11 lg:block">
            <span className="block text-sm text-white opacity-50">Frameworks</span>
            <ul className={FOOTER_LINK_LIST_FRAMEWORKS}>
              {data.frameworks.map((item: ItemObject, index: number) => {
                const key = `i-${index}`
                return (
                  <li key={key}>
                    <PrismicLink field={item.link_url}>{item.link_title}</PrismicLink>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        {/* For Responsive Mobile Version */}
        <div className="mt-0 block lg:hidden">
          <span className="block text-sm text-white opacity-50">Frameworks</span>
          <ul className={FOOTER_LINK_LIST_FRAMEWORKS}>
            {data.frameworks.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <li key={key}>
                  <PrismicLink field={item.link_url}>{item.link_title}</PrismicLink>
                </li>
              )
            })}
          </ul>
        </div>

        <div>
          <span className="block text-sm text-white opacity-50">Partners</span>
          <ul className={FOOTER_LINK_LIST}>
            {data.partners.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <li key={key}>
                  <PrismicLink field={item.link_url}>{item.link_title}</PrismicLink>
                </li>
              )
            })}
          </ul>

          {/* For Desktop Version */}
          <div className="hidden lg:mt-11 lg:block">
            <span className="block text-sm text-white opacity-50">Company</span>
            <ul className={FOOTER_LINK_LIST}>
              {data.company.map((item: ItemObject, index: number) => {
                const key = `i-${index}`
                return (
                  <li key={key}>
                    <PrismicLink field={item.link_url}>
                      {item.link_title}
                      {item.is_highlighted && (
                        <span className="ml-2 text-sm text-primary-green-85">{item.highlighted_text}</span>
                      )}
                    </PrismicLink>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        {/* For Responsive Mobile Version */}
        <div className="mt-0 block lg:hidden">
          <span className="block text-sm text-white opacity-50">Company</span>
          <ul className={FOOTER_LINK_LIST}>
            {data.company.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <li key={key}>
                  <PrismicLink field={item.link_url}>
                    {item.link_title}
                    {item.is_highlighted && (
                      <span className="ml-2 text-sm text-primary-green-85">{item.highlighted_text}</span>
                    )}
                  </PrismicLink>
                </li>
              )
            })}
          </ul>
        </div>

        <div>
          <span className="block text-sm text-white opacity-50">Resources</span>
          <ul className={FOOTER_LINK_LIST}>
            {data.resources.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <li key={key}>
                  <PrismicLink field={item.link_url}>{item.link_title}</PrismicLink>
                </li>
              )
            })}
            {MARKETPLACE_RESOURCES_LINKS.map((item) => (
              <li key={item.href}>
                <Link href={localizedHref(locale, item.href)}>{item.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <span className="block text-sm text-white opacity-50">Support</span>
          <ul className={FOOTER_LINK_LIST_SUPPORT}>
            {data.support.map((item: ItemObject, index: number) => {
              const key = `i-${index}`
              return (
                <li key={key}>
                  <PrismicLink field={item.link_url}>{item.link_title}</PrismicLink>
                  <PrismicNextImage field={item.icon} width={48} height={48} loading="lazy" fallbackAlt="" />
                  {item.is_highlighted && (
                    <span className="text-sm text-primary-green-85">{item.highlighted_text}</span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    )
  },
)

FooterLinks.displayName = 'FooterLinks'
export default FooterLinks
